import { createSelector } from "@reduxjs/toolkit";
import { choSearch, registQuery } from "../../functions/searchFunctions";
import { LOCALES } from "../../constants";

const ATTR_BASIC_SCORE = {
    NUAT_ORDER: 1,
    NUAT_CHAOS: 2,
    NUAT_SUN: 3,
    NUAT_MOON: 4,
    NUAT_STAR: 5,
};

const RARITY_BASIC_SCORE = {
    SR: 1,
    SSR: 2,
};

const selectLang = (state) => state.lang;

export const selectSaviorState = (state) => state.saviors;

export const selectSaviorIds = (state) => selectSaviorState(state).ids;

export const selectSaviorEntities = (state) => selectSaviorState(state).entities;

export const selectSaviorFilter = (state) => selectSaviorState(state).filter;

const filterByRarity = (savior, rarity) => {
    if (rarity.value === "all") return true;
    return savior.rank === rarity.value;
};

const filterByAttr = (savior, attr) => {
    if (attr.value === "all") return true;
    return savior.attr === attr.value;
};

const filterByClass = (savior, classFilter) => {
    if (classFilter.value === "all") return true;
    return savior.class === classFilter.value;
};

const searchByText = (lang, savior, query) => {
    const q = String(query).trim();
    if (q === "") return true;

    registQuery(lang, q);
    const searchText = lang === LOCALES.KO ? savior.searchText : savior.searchText_ENG;
    return choSearch(searchText);
};

const sortByOrder = (a, b, lang, order) => {
    if (order.value === "asc") {
        return a.name[lang].localeCompare(b.name[lang], lang);
    } else if (order.value === "desc") {
        return b.name[lang].localeCompare(a.name[lang], lang);
    } else if (ATTR_BASIC_SCORE[a.attr] === ATTR_BASIC_SCORE[b.attr]) {
        return a.name[lang].localeCompare(b.name[lang], lang);
    } else if (RARITY_BASIC_SCORE[b.rank] === RARITY_BASIC_SCORE[a.rank]) {
        return ATTR_BASIC_SCORE[a.attr] - ATTR_BASIC_SCORE[b.attr];
    }
    return RARITY_BASIC_SCORE[b.rank] - RARITY_BASIC_SCORE[a.rank];
};

export const selectFilteredSaviors = createSelector(
    [selectSaviorIds, selectSaviorEntities, selectSaviorFilter, selectLang, (_, query) => query],
    (ids, entities, filter, lang, query) => {
        return ids
            .map((id) => entities[id])
            .filter((savior) => filterByRarity(savior, filter.rarity))
            .filter((savior) => filterByAttr(savior, filter.attr))
            .filter((savior) => filterByClass(savior, filter.class))
            .filter((savior) => searchByText(lang.value, savior, query))
            .sort((a, b) => sortByOrder(a, b, lang.value, filter.order));
    },
);

export const selectSaviorById = createSelector(
    [selectSaviorEntities, (_, saviorId) => saviorId],
    (entities, saviorId) => entities[saviorId],
);

export const selectSaviorsByRarity = createSelector(
    [selectSaviorIds, selectSaviorEntities, (_, rarity) => rarity],
    (ids, entities, rarity) => ids.map((id) => entities[id]).filter((savior) => savior.rank === rarity),
);

export const selectSaviorsByAttr = createSelector([selectSaviorIds, selectSaviorEntities, (_, attr) => attr], (ids, entities, attr) =>
    ids.map((id) => entities[id]).filter((savior) => savior.attr === attr),
);
